import { Icon, IconButton, IDropdownOption, Stack, Text, TextField } from '@fluentui/react';
import clsx from 'clsx';
import { DocumentEntityList } from 'common/components/DocumentEntityList';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { EntityDocumentValues } from '../types';
import { CustomDropDown } from './CustomDropDown';
import { EntityDocumentsPersonalPool_entityDocuments_nodes } from '../__generated__/EntityDocumentsPersonalPool';
import { FormView } from './FormView';
import { useStyles } from './index.styles';
import { DocumentTypeOptions } from 'common/types/utility';

export interface DocumentDetailPanleFormFieldsProps {
  values: EntityDocumentValues;
  fileSelected: File[];
  setFileSelected: React.Dispatch<React.SetStateAction<File[]>>;
  documentOptions?: DocumentTypeOptions[];
  companyCurrencies?: IDropdownOption[];
  documentData?: EntityDocumentsPersonalPool_entityDocuments_nodes | null;
  defaultDocumentTypeId?: number;
  isDocumentProtected?: boolean;
  isEdit: boolean;
  documentType?: DocumentTypeOptions;
  setDocumentType: (data: DocumentTypeOptions) => void;
  isAccountingDocument: boolean;
  setIsAccountingDocument: (v: boolean) => void;
  isDisabled: boolean;
  setIsDisabled: (v: boolean) => void;
}

export const DocumentDetailPanleFormFields: React.FC<DocumentDetailPanleFormFieldsProps> = ({
  values,
  fileSelected,
  setFileSelected,
  documentOptions,
  companyCurrencies,
  documentData,
  defaultDocumentTypeId,
  isDocumentProtected = false,
  isEdit,
  documentType,
  setDocumentType,
  isAccountingDocument,
  setIsAccountingDocument,
  isDisabled,
  setIsDisabled,
}) => {
  const styles = useStyles();
  const [dataExtractModelOptions, setDataExtractModelOptions] = useState<IDropdownOption[]>([]);
  const [hideExtractionDropdown, setHideExtractionDropdown] = useState(false);
  const [expandDocumentContent, setExpandDocumentContent] = useState(
    false
  );
  const multipleIsAccountingDocumentSelected = isAccountingDocument && fileSelected.length > 1;
  const { isImageDescriptionExists, extendedDescription, isAlternateDescriptionExists, isTranslatedDescriptionExists,
    alternateDescription, sourceLanguage, translatedDescription } = { ...documentData?._documentTextExtension }
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => setFileSelected((prev) => [...prev, ...files]),
    noDragEventsBubbling: true,
    multiple: true,
  });

  const onRemoveFile = (fileIndex: number) => {
    setFileSelected((prev) => prev.filter((_, index) => fileIndex !== index));
  };

  useEffect(() => {
    if (documentData?.id) {
      setIsAccountingDocument(
        documentData.documentTypes?.isAccountingDocument || false
      );
      const matchedType = documentOptions?.find(
        (opt) => opt.key === documentData.entityDocumentTypeId
      );
      if (matchedType) setDocumentType(matchedType);
    }
  }, [documentData, documentOptions]);

  return (
    <Stack tokens={{ childrenGap: 20 }} className={styles.container}
      styles={{
        root: {
          marginTop: '30px',
        },
      }}
    >
      {documentOptions && (
        <CustomDropDown
          isEdit={isEdit}
          defaultDocumentTypeId={defaultDocumentTypeId}
          documentData={documentData || null}
          isDocumentProtected={isDocumentProtected}
          documentOptions={documentOptions}
          setDocumentType={(data) => setDocumentType(data)}
          setFileSelected={() => setFileSelected([])}
          setIsAccountingDocument={(isAccounting) => setIsAccountingDocument(isAccounting!)}
          disabled={isDisabled}
          onDocumentTypeChange={setDataExtractModelOptions}
          hideExtractionDropdown={() => setHideExtractionDropdown(true)}
        />
      )}

      {!isEdit && values.entityDocumentTypeId && (
        <div
          {...getRootProps()}
          className={clsx(styles.dropZone, isDragActive && styles.dropZoneHover)}
        >
          <Stack grow horizontalAlign="center" verticalAlign="center" verticalFill>
            <input {...getInputProps({ multiple: true })} />
            {isDragActive ? (
              <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Icon iconName={'CloudUpload'} className={styles.uploadIconStyle} />
                <Text className={styles.uploadIconStyle}>Drop the files here...</Text>
              </Stack>
            ) : (
              <Text>Drag 'n' drop some files here, or click to select files</Text>
            )}
          </Stack>
        </div>
      )}

      {fileSelected.length > 0 && (
        <DocumentEntityList onRemoveFile={onRemoveFile} filesList={fileSelected!} />
      )}

      <FormView
        isAccountingDocument={isAccountingDocument}
        isDocumentProtected={isDocumentProtected}
        showIsAccountingDocumentInputs={!multipleIsAccountingDocumentSelected}
        documentData={documentData}
        documentType={documentType}
        companyCurrencies={companyCurrencies}
        onInputFieldsDisabled={setIsDisabled}
        dataExtractModelOptions={dataExtractModelOptions}
        isEdit={isEdit}
        hideExtractionDropdown={hideExtractionDropdown}
      />
      {!!documentData?._isDocumentTextExtensionExists &&
        <Stack tokens={{ childrenGap: 20 }}>
          <Stack horizontal tokens={{ childrenGap: 10 }} style={{ marginTop: 20 }}>
            <Text variant="xLarge"  >
              Document Content
            </Text>
            {!expandDocumentContent ? (
              <IconButton
                onClick={() => setExpandDocumentContent(true)}
                iconProps={{ iconName: 'ChevronDown' }}
                className={styles.iconButtonColor}
              />
            ) : (
              <IconButton
                onClick={() => setExpandDocumentContent(false)}
                iconProps={{ iconName: 'ChevronUp' }}
                className={styles.iconButtonColor}
              />
            )}
          </Stack>
          {expandDocumentContent && <>
            <TextField
              multiline
              label={isImageDescriptionExists ? 'Image' : 'Full Text'}
              value={extendedDescription ?? ''}
              resizable={false}
              className={extendedDescription ? styles.textAreaField : undefined}
            />
            {isAlternateDescriptionExists && <TextField
              multiline
              label={'Full Text'}
              value={alternateDescription ?? ''}
              resizable={false}
              className={extendedDescription ? styles.textAreaField : undefined}
            />}
            {isTranslatedDescriptionExists && <TextField
              multiline
              label={sourceLanguage ?? ''}
              value={translatedDescription ?? ''}
              resizable={false}
              className={extendedDescription ? styles.textAreaField : undefined}
            />}
          </>}
        </Stack>

      }
    </Stack>
  );
};